import products from "@/api/products";
import shop from "@/api/shop";
import {toNumericId} from "@/api/utils";
import {sleep} from "@/utils/helpers";
import platformSettings from '~config/settings'
import {sameShopifyId} from "@/utils/inventory";

const state = () => ({
    product: {},
    productList: [],
    currency: undefined,
    isFetched: false,
    isReady: false,
    search: '',
    currentPage: 1,
    listScrollPosition: undefined
})

const getters = {}

const actions = {
    async fetchProduct({commit}, {productId, isUpdated}) {
        const product = await products.getProduct(productId)
        const toExclude = platformSettings.productCatsExclude || []
        if (product.options.length === 1 && product.options[0].name === 'Title' && product.options[0].values[0] === 'Default Title') {
            product.options[0].values[0] = product.title
            product.variants[0].title = product.title
            product.variants[0].selectedOptions[0].value = product.title
        }


        if (toExclude.length > 0 && product.collections) {
            product.collections = product.collections.filter(e => !toExclude.includes(e.title))
        }

        commit('setProduct', product)
        if (isUpdated) {
            commit('updateProductList', product)
        }
    },
    // Re-reads only the inventory levels, so unsaved edits elsewhere on the page survive.
    async refreshInventory({commit}, {productId}) {
        const product = await products.getProduct(productId)
        const variants = product.variants || []
        variants.forEach(variant => {
            (variant.inventoryItem.inventoryLevels || []).forEach(level => {
                commit('setInventoryQuantity', {
                    inventoryItemId: variant.inventoryItem.id,
                    locationId: level.location.id,
                    quantity: level.quantities[0].quantity
                })
            })
        })
    },
    async fetchProductList({commit, dispatch, state}) {
        let cursor = undefined
        let counter = 0
        const fetchAll = async () => {
            await products.get(10, cursor !== undefined ? cursor : '').then(async e => {
                console.log("Products:", e)
                if (e.length === 0) return
                const items = e.data.map(e => {
                    counter += 1
                    return {
                        count: counter,
                        hasZeroQuantity: e.variants.filter(x => x.inventoryQuantity < 10).length > 0,
                        ...e
                    }
                })
                commit('setProductList', items)
                state.isFetched = true
                await sleep(750)
                if (e.pageInfo.hasNextPage === true) {
                    cursor = e.pageInfo.nextCursor
                    await fetchAll()
                } else {
                    state.isReady = true
                }
            }).catch(async (error) => {
                if (error.response) {
                    const e = error.response.data
                    if (!e.body.errors) return
                    const code = e.body.errors[0].extensions.code
                    if (code === 'THROTTLED') {
                        await sleep(1250).finally(async () => await fetchAll());
                    } else {
                        state.isFetched = true
                    }
                }
            })
        }
        fetchAll().catch((e) => {
            console.log(e, e.status)
            state.isFetched = true
        })
        if (state.currency === undefined || state.currency === '') {
            await dispatch('fetchCurrency')
        }
    },
    async fetchCurrency({commit}) {
        const shopData = await shop.get().then(this.setCurrency)
        const currencyMapping = {EUR: '€', CHF: 'CHF'}
        commit('setCurrency', currencyMapping[shopData.currencyCode])
    }
}

const mutations = {
    setSearch(state, string) {
        state.search = string
    },
    setProduct(state, product) {
        state.product = product
    },
    setCurrentPage(state, item) {
        state.currentPage = item
    },
    setProductList(state, products) {
        const prods = products.filter(e => !state.productList.map(e => e.id).includes(e.id))
        state.productList.push(...prods)
    },
    setListScroll(state, val) {
        state.listScrollPosition = val
    },
    async updateProductList(state, product) {
        if (!product.id) return
        const idNumeric = toNumericId(product.id)
        const currIndex = state.productList.findIndex(e => e.id === idNumeric)
        product.id = idNumeric
        const hasZeroQuantity = product.variants && product.variants.filter(x => x.inventoryQuantity < 10).length > 0 || false;
        if (currIndex === -1) {
            state.productList.unshift(
                {
                    isNew: true,
                    hasZeroQuantity,
                    ...product
                }
            )
            state.currentPage = 1
        } else if (currIndex > -1) {
            state.productList[currIndex] = {
                isUpdated: true,
                count: currIndex + 1,
                hasZeroQuantity,
                ...product
            }
        }
    },
    // Updates the stored quantity that the next save sends as changeFromQuantity.
    setInventoryQuantity(state, {inventoryItemId, locationId, quantity}) {
        const variants = [state.product, ...state.productList].flatMap(p => (p && p.variants) || [])
        variants.filter(v => v.inventoryItem && sameShopifyId(v.inventoryItem.id, inventoryItemId)).forEach(variant => {
            const levels = variant.inventoryItem.inventoryLevels || []
            const level = levels.find(l => sameShopifyId(l.location.id, locationId))
            if (!level) return
            level.quantities[0].quantity = quantity
            if (levels.length === 1) {
                variant.inventoryQuantity = quantity
            }
        })
    },
    setCurrency(state, c) {
        state.currency = c
    },
    setTags(state, {tags}) {
        state.product.tags = tags
    },
    setFetched(state, c) {
        state.isFetched = c
    },
    removeProduct(state, productId) {
        const idNumeric = toNumericId(productId)
        state.productList = state.productList.filter(e => e.id !== idNumeric)
    }
}


export default {
    namespaced: true, state, getters, actions, mutations
}
